import { TabMenuItems } from "@/lib/types";

/**
 * Contrat de l'import de donnees.
 *
 * Miroir des types de l'API (`inventozen-api/src/api/data-import/`). Le
 * back-office ne rejoue **pas** sa propre configuration de champs : le
 * catalogue est servi par `GET /back/api/data-import/:table/fields`, parce que
 * le parseur cote serveur ne doit jamais faire confiance a un type declare par
 * le client.
 */

export type ImportTable = TabMenuItems;

/**
 * Tables réellement ouvertes à l'import Excel.
 *
 * Temporairement limité à « Œuvre » : les autres types restent gérés par le
 * back-office mais ne sont pas encore proposés à l'import. Centralisé ici pour
 * que le sélecteur ET le wizard (validation de l'URL `?table=`) partagent la
 * même liste — sinon un lien `?table=author` ferait planter l'étape 1.
 */
export const IMPORTABLE_TABLES: readonly ImportTable[] = ["artwork"];

/** Cette table est-elle ouverte à l'import ? */
export const isImportableTable = (value: string | null): value is ImportTable =>
  value != null && (IMPORTABLE_TABLES as readonly string[]).includes(value);

export type ImportTemplateProfile = "basic" | "full";

/** Semantique d'un champ, telle qu'exposee par l'API. */
export type ImportFieldKind =
  | "string"
  | "text"
  | "boolean"
  | "number"
  | "currency"
  /** montant importé comme estimation liée (MonetaryTransaction) */
  | "estimation"
  | "date"
  | "multilingual"
  | "dimensions"
  | "list"
  | "enum"
  | "relation"
  | "ignored";

export interface ImportField {
  key: string;
  label: string;
  kind: ImportFieldKind;
  required: boolean;
  profile: ImportTemplateProfile;
  target?: string;
  allowCreate?: boolean;
  listKey?: string;
  options?: string[];
  multilingual?: boolean;
  ignoredReason?: string;
}

/** Etat du rapprochement d'une colonne du fichier. */
export type ImportColumnState =
  /** rapprochee avec certitude */
  | "matched"
  /** plusieurs champs portent cet intitule */
  | "ambiguous"
  /** rapprochement approximatif, a confirmer */
  | "uncertain"
  /** aucun rapprochement */
  | "unknown";

export interface ImportColumnCandidate {
  fieldKey: string;
  label: string;
  score: number;
}

export interface ImportAnalysedColumn {
  columnIndex: number;
  /** lettre de colonne Excel, pour que l'utilisateur la retrouve dans son fichier */
  letter: string;
  header: string;
  state: ImportColumnState;
  fieldKey: string | null;
  fieldLabel: string | null;
  lang?: string;
  subKey?: string;
  /** l'utilisateur a explicitement choisi d'ignorer cette colonne */
  ignored?: boolean;
  kind: ImportFieldKind | null;
  filled: number;
  samples: string[];
  candidates: ImportColumnCandidate[];
}

export interface ImportFillRateEntry {
  fieldKey: string | null;
  header: string;
  label: string;
  filled: number;
  /** 0..1 */
  rate: number;
  samples: string[];
  /** renseigne pour les colonnes de liaison */
  distinctCount?: number;
  isRelation: boolean;
}

export interface ImportHighlight {
  kind: "topValue" | "topFilledField";
  /** clé i18n `bo.import.server.highlight.*` */
  messageKey?: string;
  fieldKey: string | null;
  label: string;
  value: string | null;
  count: number;
  share: number;
  total?: number;
  pct?: number;
  /** @deprecated anciens jobs : phrase précomposée */
  message?: string;
}

export interface ImportWarning {
  code: string;
  /** @deprecated préférer `params` + traduction client */
  message?: string;
  params?: Record<string, string | number>;
  /** un avertissement bloquant interdit de continuer sans intervention */
  blocking: boolean;
}

export interface ImportAnalysisReport {
  version: 1;
  sheetName: string;
  otherSheetNames: string[];
  templateVersion: string | null;
  /** true quand la correspondance des colonnes s'est resolue seule */
  mappingResolved: boolean;

  rowCount: number;
  columnCount: number;
  mappedColumnCount: number;
  ignoredColumns: string[];
  emptyColumns: string[];

  columns: ImportAnalysedColumn[];

  fillRate: ImportFillRateEntry[];
  highlights: ImportHighlight[];

  duplicates: {
    withinFile: Array<{ matchKey: string; rowIndexes: number[] }>;
  };
  missingRequired: Array<{ fieldKey: string; label: string }>;

  warnings: ImportWarning[];
  disclaimers: string[];
}

/** Statut d'un import, miroir de l'enum Prisma. */
export type ImportJobStatus =
  | "uploaded"
  | "analyzing"
  | "awaitingMapping"
  | "awaitingDecisions"
  | "ready"
  | "queued"
  | "committing"
  | "completed"
  | "completedWithErrors"
  | "failed"
  | "cancelled"
  | "expired";

export type ImportWriteMode = "createOnly" | "createAndUpdate";

/** Etat d'une question de rapprochement. */
export type ImportDecisionState = "pending" | "auto" | "resolved" | "ignored";

export interface ImportDecisionSuggestion {
  id: string | null;
  label: string;
  hint?: string;
  score: number;
  reason: string;
}

export interface ImportDecision {
  id: string;
  kind: "relationValue" | "listValue" | "enumValue";
  state: ImportDecisionState;
  columnKey: string;
  rawValue: string;
  normValue: string;
  suggestions: ImportDecisionSuggestion[];
  bestScore: number | null;
  /** index des lignes concernees : « affecte 37 lignes » */
  rowRefs: number[];
  occurrences: number;
  resolution: {
    action: "link" | "value" | "create" | "skip";
    targetId?: string;
    value?: string;
    label?: string;
  } | null;
}

export interface ImportDecisionGroup {
  columnKey: string;
  label: string;
  total: number;
  decided: number;
  pending: number;
}

/** Reponse a une question. */
export interface ImportDecisionAnswer {
  id: string;
  action: "link" | "value" | "create" | "skip";
  targetId?: string;
  value?: string;
  remember?: boolean;
}

export interface ImportJob {
  jobId: string;
  table: ImportTable;
  status: ImportJobStatus;
  writeMode: ImportWriteMode;
  fileName: string;
  totalRows: number;
  processedRows: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  pendingDecisionCount: number;
  /** nombre de fiches liées (auteur…) qui seront créées à l'import */
  linkedToCreateCount?: number;
  /** fiches liées réellement créées, par type (author/collector/ensemble) */
  linkedCreatedByType?: Record<string, number>;
  errorMessage: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  /** horodatage d'annulation de l'import, si les fiches créées ont été supprimées */
  undoneAt: string | null;
  report: ImportAnalysisReport | null;
}

export interface ImportProgress {
  jobId: string;
  status: ImportJobStatus;
  totalRows: number;
  processedRows: number;
  percent: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  pendingDecisionCount: number;
  errorMessage: string | null;
  /** cadence pilotee par le serveur ; 0 = arreter d'interroger */
  retryAfterMs: number;
}

export interface ImportRowReport {
  rowIndex: number;
  status: "pending" | "created" | "updated" | "skipped" | "failed";
  matchKey: string | null;
  entityId: string | null;
  issues: Array<{
    level: "error" | "warning";
    code: string;
    message: string;
    field?: string;
  }> | null;
}

/** Ce que renvoie l'analyse : un job, pas seulement un rapport. */
export interface ImportAnalyseResult {
  jobId: string;
  status: ImportJobStatus;
  pendingDecisionCount: number;
  report: ImportAnalysisReport;
}

/** Statuts terminaux : plus rien n'evoluera. */
export const IMPORT_TERMINAL_STATUSES: ImportJobStatus[] = [
  "completed",
  "completedWithErrors",
  "failed",
  "cancelled",
  "expired",
];

export function isTerminal(status?: ImportJobStatus): boolean {
  return status != null && IMPORT_TERMINAL_STATUSES.includes(status);
}

/** Les etapes du wizard, dans l'ordre. */
export const IMPORT_STEPS = [
  "table",
  "template",
  "upload",
  "review",
  "relations",
  "confirm",
  "running",
  "report",
] as const;
export type ImportStep = (typeof IMPORT_STEPS)[number];

/** Extensions acceptees a l'upload, pour react-dropzone. */
export const IMPORT_FILE_ACCEPT: Record<string, string[]> = {
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
};

/** Meme plafond que l'API : refuser cote client evite un upload inutile. */
export const IMPORT_MAX_SIZE_MB = 20;
/** Au-dela, on previent que l'analyse prendra un moment. */
export const IMPORT_SLOW_SIZE_MB = 8;

/** Un avertissement bloquant empeche-t-il de continuer ? */
export function hasBlockingWarning(report?: ImportAnalysisReport): boolean {
  if (!report) return false;
  return (
    report.warnings.some((warning) => warning.blocking) ||
    report.missingRequired.length > 0
  );
}

/** Colonnes qui demandent une intervention de l'utilisateur. */
export function columnsNeedingAttention(
  report?: ImportAnalysisReport,
): ImportAnalysedColumn[] {
  if (!report) return [];
  return report.columns.filter(
    (column) => column.state !== "matched" && column.header,
  );
}
